import { lazy } from "react";
import HTTPRequest from "../Utils/HTTPRequest";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from "@arcgis/core/widgets/Expand";
import Basemap from "@arcgis/core/Basemap";
import Bookmarks from "@arcgis/core/widgets/Bookmarks";
import Legend from "@arcgis/core/widgets/Legend";
import Fullscreen from "@arcgis/core/widgets/Fullscreen";
import Home from "@arcgis/core/widgets/Home";
import Portal from "@arcgis/core/portal/Portal";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import PortalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/PortalBasemapsSource";
import i18next, { changeLanguage } from "i18next";
import Extent from "@arcgis/core/geometry/Extent";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import LayerList from "@arcgis/core/widgets/LayerList";
import Print from "@arcgis/core/widgets/Print";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Search from "@arcgis/core/widgets/Search";

const CommonFunctions = {
  InitializeViewEvents(view, that, layer) {
    /* when user clicks on graphic highlight the feature on gallery and left side */
    view.on("click", (event) => {
      view.popup.fetchFeatures(event.screenPoint).then(function (response) {
        //Access the response from fetchFeatures
        response.allGraphicsPromise.then(function (graphics) {
          //Set the feature widget's graphic to the returned graphic from fetchFeatures
          if (typeof graphics != "undefined") {
            layer.graphics.remove();

            var markerSymbol = {
              type: "picture-marker",
              url: "images/top20.gif",
              width: 38,
              height: 38,
            };
            graphics[0].symbol = markerSymbol;
            var id = graphics[0].attributes.id - 1;
            that.setState({ activeIndex: id });
          }
        });
      });
    });

    /*show the info window when mouse hover on the feature */
    view.on("pointer-move", function (event) {
      view.hitTest(event).then(function (response) {
        if (response.results.length) {
          var graphic = response.results[0].graphic;
          if (typeof graphic != "undefined") {
            if (graphic.geometry != null) {
              view.popup.open({
                location: graphic.geometry,
                features: [graphic],
              });
            } else {
              view.popup.close();
            }
          } else {
            view.popup.close();
          }
        } else {
          view.popup.close();
        }
      });
    });
  },
  lazyWithRetry(componentImport) {
    return lazy(async () => {
      const pageHasAlreadyBeenForceRefreshed = JSON.parse(
        window.localStorage.getItem("page-has-been-force-refreshed") || "false",
      );

      try {
        const component = await componentImport();

        window.localStorage.setItem("page-has-been-force-refreshed", "false");

        return component;
      } catch (error) {
        if (!pageHasAlreadyBeenForceRefreshed) {
          // Assuming that the user is not on the latest version of the application.
          // Let's refresh the page immediately.
          window.localStorage.setItem("page-has-been-force-refreshed", "true");
          return window.location.reload();
        }

        // The page has already been reloaded
        // Assuming that user is already using the latest version of the application.
        // Let's let the application crash and raise the error.
        throw error;
      }
    });
  },
  renderAppConfig(SubThemesInfo, ThemeConfig) {
    this.appConfig = {};
    const language = sessionStorage.getItem("language") || "en";
    const isArabic = language === "ar";

    // Helper function to get language-specific value
    const getLangValue = (obj, arabicKey, englishKey) =>
      isArabic ? obj[arabicKey] : obj[englishKey];

    // Initialize main subTheme
    const firstTheme = SubThemesInfo[0];
    const subThemeName = firstTheme.SubThemeName.toLowerCase();
    const subTheme = (this.appConfig[subThemeName] = {});

    // Set basic subTheme properties
    subTheme.cmsPageId = getLangValue(firstTheme, "CmsPageId_ar", "CmsPageId");
    subTheme.Title = firstTheme.Title;
    subTheme.Title_ar = firstTheme.Title_ar;

    // Find active index
    const themeIndex = ThemeConfig.findIndex(
      (theme) => theme.SubThemeName.toLowerCase() === subThemeName,
    );
    if (themeIndex !== -1) {
      subTheme.activeindex = themeIndex;
    }

    const processedSections = new Set();

    SubThemesInfo.forEach((themeInfo, index) => {
      const { SectionName, SubSectionName, MapDivId, UniqueIdentifier } =
        themeInfo;
      let section, subSection;

      // Handle section creation/access
      if (SubSectionName && SubSectionName !== "") {
        if (!processedSections.has(SectionName)) {
          section = subTheme[SectionName] = {};
          section.mapDivId = MapDivId;
          section.name = SectionName;
          processedSections.add(SectionName);
        } else {
          section = subTheme[SectionName];
        }

        // Create subsection
        subSection = section[SubSectionName] = {};
        subSection.tabcls = SectionName;
        subSection.sectionname = themeInfo.UniqueIdentifier;
        subSection.title = getLangValue(themeInfo, "ItemTitle_ar", "ItemTitle");
        subSection.name = getLangValue(themeInfo, "ItemName_ar", "ItemName");
      } else {
        // No subsection - create section directly
        subSection = subTheme[SectionName] = {};
        subSection.mapDivId = MapDivId;
        subSection.name = UniqueIdentifier;
      }

      // Set common properties
      Object.assign(subSection, {
        mapType: themeInfo.ComponentType,
        ShowBasemap: themeInfo.ShowBasemap,
        ShowToc: themeInfo.ShowToc,
        ShowMapScale: themeInfo.ShowMapScale,
        ShowPrint: themeInfo.ShowPrint,
        ShowOverView: themeInfo.ShowOverView,
        ShowSearch: themeInfo.ShowSearch,
        ShowLegend: themeInfo.ShowLegend,
        SeachUrl: themeInfo.SEARCH_URL,
        SeachFields: getLangValue(
          themeInfo,
          "SEARCH_FIELDS_AR",
          "SEARCH_FIELDS",
        ),
        DisplayFields: getLangValue(
          themeInfo,
          "DISPLAY_FIELD_AR",
          "DISPLAY_FIELD",
        ),
        PlaceHolder: getLangValue(
          themeInfo,
          "PLACEHOLDER_NAME_AR",
          "PLACEHOLDER_NAME",
        ),
        SearchResultsTitle: getLangValue(themeInfo, "NAME_AR", "NAME"),
      });

      // Feature configuration for Map types
      const isMapType = [
        "Map",
        "StoryMapHorizontal",
        "StoryMapVertical",
      ].includes(themeInfo.ComponentType);
      if (isMapType) {
        const featureLabelsKey =
          themeInfo.ComponentType === "Map"
            ? "DisplayFieldsLabels_ar"
            : "DisplayFieldsLabels_en";

        Object.assign(subSection, {
          FeatureDisplayFieldsLabels: getLangValue(
            themeInfo,
            featureLabelsKey,
            "DisplayFieldsLabels_en",
          ),
          FeatureDisplayFields: getLangValue(
            themeInfo,
            "DisplayFields_ar",
            "DisplayFields_en",
          ),
          FeatureListViewFields: getLangValue(
            themeInfo,
            "ListViewFields_ar",
            "ListViewFields_en",
          ),
          FeatureTitle: getLangValue(themeInfo, "Title_ar1", "Title_en"),
          FeatureQueryFields: themeInfo.QueryFields,
          FeatureOrderbyField: themeInfo.OrderbyField,
          FeatureSearchField: themeInfo.QuerySearchField,
          FeatureOrderbyType: themeInfo.OrderbyType,
          FeatureLayerTitle: getLangValue(
            themeInfo,
            "Layer_title_ar",
            "Layer_title",
          ),
        });
      }

      // SwipeWidget configuration
      if (themeInfo.ComponentType === "SwipeWidget") {
        Object.assign(subSection, {
          LeadingLayer: getLangValue(
            themeInfo,
            "LeadingLayer_ar",
            "LeadingLayer_en",
          ),
          TrailingLayer: getLangValue(
            themeInfo,
            "TrailingLayer_ar",
            "TrailingLayer_en",
          ),
          Center: themeInfo.Center,
          BMUrl: themeInfo.BMUrl,
          MaxScale: themeInfo.MaxScale,
        });
      }

      // Source details
      subSection.source = this.GetSourceDetails(themeInfo, language);
      subSection.sourceForExcel = this.GetSourceDetails(
        themeInfo,
        language,
        true,
      );

      // Map settings or dashboard URL
      const isDashboardType = ["Dashboard", "Chart", "LineChart"].includes(
        themeInfo.MapType,
      );
      if (isDashboardType) {
        subSection.dashboardurl = getLangValue(
          themeInfo,
          "PortalUrl_ar",
          "PortalUrl",
        );
      } else {
        subSection.mapId = index;
        subSection.mapSettings = {
          portal: {
            url: getLangValue(themeInfo, "PortalUrl_ar", "PortalUrl"),
            portalItemId: getLangValue(
              themeInfo,
              "PortalItemId_ar",
              "PortalItemId",
            ),
            basemapurl: getLangValue(themeInfo, "BaseMapUrl_ar", "BaseMapUrl"),
            minZoom: themeInfo.MinZoom,
            maxZoom: themeInfo.MaxZoom,
            isPan: themeInfo.isPan,
            zoomLevel: themeInfo.zoomlevel,
            referenceLayer: getLangValue(
              themeInfo,
              "ReferenceLayer_ar",
              "ReferenceLayer_en",
            ),
          },
        };
      }
    });

    console.log("appConfig: ", this.appConfig);
    return this.appConfig;
  },
  renderColors() {
    var colors = [];
    let pathname = window.location.pathname;
    let patharray = pathname.split("/");
    let theme = null;
    if (patharray.length > 2 && process.env.REACT_APP_BASE_URL == "") {
      theme = patharray[1];
    } else if (patharray.length > 3 && process.env.REACT_APP_BASE_URL != "") {
      theme = patharray[2];
    }
    if (theme != null) {
      theme = theme.toLowerCase();
      if (theme == "health") {
        colors = ["#5fd6de", "#5991cf", "#8d5a4d"];
      } else if (theme == "womenempowerment") {
        colors = ["#de8ba5", "#5fd6de"];
      } else if (theme == "environment") {
        colors = ["#5fd6de", "#5991cf"];
      } else if (theme == "communityempowerment") {
        colors = [
          "#5f27cd",
          "#ff9ff3",
          "#f7b731",
          "#ff6b6b",
          "#4ecdc4",
          ,
          "#693ca6",
          "#00d2d3",
        ];
      } else if (theme == "education") {
        colors = [
          "#579BB2",
          "#57C7EC",
          "#289EC9",
          "#47BBE6",
          "#5AAEE6",
          "#2C8CD5",
          "#579DDF",
          "#3198BA",
          "#52AFCE",
        ];
      } else if (theme == "population") {
        colors = [
          "#5491c2",
          "#92c9f6",
          "#6993b5",
          "#4f83ae",
          "#88c2f1",
          "#61a7e1",
          "#95c0e6",
          "#5793c4",
          "#5687b0",
          "#679dc9",
          "#6d8eaa",
        ];
      } else if (theme == "economy") {
        colors = [
          "#bcb260",
          "#d5c971",
          "#bdb25a",
          "#c7be7a",
          "#9b9143",
          "#dcd39a",
          "#d1c250",
          "#dacc60",
          "#aca258",
          "#9f933b",
        ];
      } else if (theme == "telecomandspace") {
        colors = [
          "#174fa7",
          "#2d76e7",
          "#6595e2",
          "#5a67dc",
          "#2566ce",
          "#2672ec",
          "#288fdd",
          "#4389cb",
          "#247cb6",
          "#1d489a",
        ];
      } else if (theme == "energy") {
        colors = [
          "#ffe052",
          "#c1a115",
          "#f0d976",
          "#f1ca1c",
          "#d5b529",
          "#f0e5ae",
          "#eede97",
          "#f3d656",
          "#dfb600",
          "#ddc144",
        ];
      } else if (theme == "regionalandinternationalcontext") {
        colors = [
          "#8d5a4d",
          "#df9582",
          "#d86d52",
          "#ae4c33",
          "#a4786d",
          "#d89e91",
          "#8a5e52",
          "#80362b",
          "#9a5544",
          "#c1a199",
        ];
      } else if (theme == "smartcities") {
        colors = ["#8d5a4d"];
      }
    }
    return colors;
  },
  GetSourceDetails(result, language, forExcel) {
    var source = " ";
    var datasource =
      language == "ar" ? result.Data_Source_Ar : result.Data_Source;
    var sourceurl = language == "ar" ? result.Source_URL_Ar : result.Source_URL;
    var shorturl = language == "ar" ? result.Short_URL_Ar : result.Short_URL;
    var LastUpdateDate = result.LastUpdatedDate;
    if (result.IsOpenData == null) {
      source = language == "ar" ? result.Source_ar : result.Source;
    } else {
      if (result.IsOpenData == "Yes") {
        source = source + i18next.t("OpenData") + datasource;
      }
      if (result.IsOpenData == "No") {
        source = source + datasource;
      }
      if (sourceurl != null && sourceurl != "" && !forExcel) {
        /*  var sourceurls= sourceurl.split(',');
            var shorturls=shorturls.split(',');
            if(sourceurls.length > 1)
            {
                for(var i=0;i<sourceurls.length;i++)
                {
                  source=source+", <a  href='"+ sourceurls[i]+"' target='_blank'> "+shorturls[i] +"</a>";
                }
            }
            else
            { */
        source =
          source +
          ", <a  href='" +
          sourceurl +
          "' target='_blank'> " +
          shorturl +
          "</a>";
        /*  } */
      }
      if (LastUpdateDate != null && LastUpdateDate != "") {
        source = source + ", " + i18next.t("LastUpdate") + LastUpdateDate;
      }
    }

    return source;
  },
  RenderRoundedData(labelValue) {
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? Math.round((Math.abs(Number(labelValue)) / 1.0e9) * 100) / 100 +
          i18next.t("B")
      : // Six Zeroes for Millions
        Math.abs(Number(labelValue)) >= 1.0e5
        ? Math.round((Math.abs(Number(labelValue)) / 1.0e6) * 100) / 100 +
          i18next.t("M")
        : // Three Zeroes for Thousands
          Math.abs(Number(labelValue)) >= 1.0e3
          ? Math.round((Math.abs(Number(labelValue)) / 1.0e3) * 100) / 100 +
            i18next.t("K")
          : Number(labelValue);
  },
  renderSubThemes(source) {
    var images = [];
    let language = sessionStorage.getItem("language");

    var result = source;
    if (result.length == 0) {
    }

    for (var i = 0; i < result.length; i++) {
      if (language === "ar")
        images.push({
          itemImageSrc: "/images/" + result[i].ImageUrl,
          href: "/" + result[i].ThemeName + "/" + result[i].SubThemeName,
          alt: result[i].SubThemeName,
          title: result[i].Title_ar,
          NoOfItemsToDisplay: result[i].NoOfItemsToDisplay,
          ThemeTitle: result[i].ThemeTitle_ar,
          ThemeName: result[i].ThemeName,
          id: i + 1,
          BackgroundImageUrl:
            process.env.REACT_APP_BASE_URL +
            "/images/" +
            result[i].BackgroundImageUrl,
        });
      else
        images.push({
          itemImageSrc: "/images/" + result[i].ImageUrl,
          href: "/" + result[i].ThemeName + "/" + result[i].SubThemeName,
          alt: result[i].SubThemeName,
          title: result[i].Title,
          NoOfItemsToDisplay: result[i].NoOfItemsToDisplay,
          ThemeTitle: result[i].ThemeTitle,
          ThemeName: result[i].ThemeName,
          id: i + 1,
          BackgroundImageUrl:
            process.env.REACT_APP_BASE_URL +
            "/images/" +
            result[i].BackgroundImageUrl,
        });
    }
    return images;
  },
  renderThemesInfo(source) {
    var ThemesInfo = [];
    let language = sessionStorage.getItem("language");

    var result = source;
    if (result.length == 0) {
      window.location.href = process.env.REACT_APP_BASE_URL + "/404";
    }

    for (var i = 0; i < result.length; i++) {
      var ThemeClass = result[i].ThumbnailImageUrl.split(".jpg");
      ThemesInfo.push({
        ThemeName: result[i].ThemeName,
        ThemeTitle:
          language === "ar" ? result[i].ThemeTitle_ar : result[i].ThemeTitle,
        href: "/" + result[i].NavigationUrl,
        image: "images/" + result[i].ThumbnailImageUrl,
        mobileImageUrl: "images/" + result[i].mobileImageUrl,
        ThemeClass: ThemeClass[0],
      });
    }
    return ThemesInfo;
  },
  getSelectedLanguage() {
    const lang = new URLSearchParams(window.location.search).get("lang");
    const validLang = lang === "en" || lang === "ar" ? lang : null;

    sessionStorage.setItem(
      "language",
      validLang || sessionStorage.getItem("language") || "ar",
    );

    return sessionStorage.getItem("language");
  },

  changeLanguage(e) {
    var lng = e.currentTarget.innerText;
    let newLang = lng === "ENGLISH" || lng === "ENG" ? "en" : "ar";
    sessionStorage.setItem("language", newLang);

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (params.get("lang")) {
      params.set("lang", newLang);
      url.search = params.toString();
    }
    window.location.href = url.toString();
  },

  // start login open for public
  getSession() {
    return true;
    // return sessionStorage.getItem("session") == null
    //   ? false
    //   : sessionStorage.getItem("session");
  },
  // end login open for public
  renderBaseMapGallery(view, position) {
    var language = this.getSelectedLanguage();
    if (typeof position == "undefined") {
      position = "top-left";
    }
    var source = "basemapconfigen";
    if (language === "ar") source = "basemapconfigar";

    HTTPRequest.getJson(
      process.env.REACT_APP_AtlasWSurl + "GetChartData?source=" + source,
      false,
      function (result) {
        const portal = new Portal({
          url: process.env.REACT_APP_portalUrl,
        });

        var portalItems = [];

        const source = new PortalBasemapsSource({
          portal,
          updateBasemapsCallback: function (items) {
            for (var i = 0; i < items.length; i++) {
              for (var j = 0; j < result.length; j++) {
                if (items[i].portalItem.id === result[j].portalitemid) {
                  portalItems.push(items[i]);
                }
              }
            }
            //console.log(portalItems);
            return portalItems;
          },
        });

        let basemapGallery = new BasemapGallery({
          view: view,
          source: source,
        });

        let bgExpand = new Expand({
          view,
          content: basemapGallery,
          expandIconClass: "esri-icon-basemap",
          //collapseIconClass: "esri-icon-collapse",
          expandTooltip: i18next.t("Basemapgallery"),
        });
        view.ui.add(bgExpand, position);
        basemapGallery.watch(
          "activeBasemap",
          function (newValue, oldValue, property, object) {
            if (bgExpand != undefined && bgExpand != null) {
              bgExpand.expanded = false;
            }
          },
        );
      },
    );
  },
  renderBookMarkWidget(view) {
    const bookmarks = new Bookmarks({
      view: view,
      editingEnabled: false,
    });
    const legend = new Expand({
      content: bookmarks,
      view: view,
      expanded: false,
    });

    view.ui.add(legend, "top-left");
    return legend;
  },
  renderHomeButtonWidget(view, applicationDiv) {
    view.ui.add(
      new Home({
        view: view,
      }),
      "top-right",
    );
  },
  renderFullScreenWidget(view, applicationDiv) {
    view.ui.add(
      new Fullscreen({
        view: view,
        element: applicationDiv,
      }),
      "top-right",
    );
  },
  renderLegendWidget(view, Istocavailable) {
    var legendlayerlist = true;

    if (isMobile) {
      legendlayerlist = false;
    }

    const legend = new Expand({
      content: new Legend({
        view: view,
        listItemCreatedFunction: (event) => {
          const item = event.item;
          if (item.layer.type != "group") {
            // don't show legend twice
            item.panel = {
              content: "legend",
              open: true,
            };
          }
        },
      }),
      view: view,
      expandTooltip: i18next.t("Legend"),
      expanded: legendlayerlist,
    });

    view.ui.add(legend, "top-left");
    return legend;
  },
  renderPlaybutton(param) {
    var elementgalleria = document.getElementById(param);
    if (
      elementgalleria !== null &&
      document.getElementById(param + "playdiv") === null
    ) {
      var element = elementgalleria.getElementsByClassName(
        "p-galleria-thumbnail-wrapper",
      );

      //Create the element using the createElement method.
      var myDiv = document.createElement("div");

      //Set its unique ID.
      myDiv.id = param + "playdiv";

      //Add your content to the DIV
      myDiv.innerHTML =
        "<button id=" +
        param +
        'playbtn  class="p-button p-component carousel-pause play play_onlytitle p-button-icon-only"><span class="p-button-icon p-c pi pi-play" id=' +
        param +
        "playspan></span></button>";
      //Finally, append the element to the HTML body
      element[0].appendChild(myDiv);
    }
  },
  renderThemeName() {
    let pathname = window.location.pathname;
    let patharray = pathname.split("/");
    let theme = null;
    if (patharray.length > 2 && process.env.REACT_APP_BASE_URL === "") {
      theme = patharray[1].toLocaleLowerCase();
    } else if (patharray.length > 3 && process.env.REACT_APP_BASE_URL !== "") {
      theme = patharray[2].toLocaleLowerCase();
    }
    return theme;
  },
  responsiveOptions(param) {
    return [
      {
        breakpoint: "1400px",
        numVisible: param >= 4 ? 4 : 3,
      },
      {
        breakpoint: "1024px",
        numVisible: 3,
      },
      {
        breakpoint: "768px",
        numVisible: 2,
      },
      {
        breakpoint: "560px",
        numVisible: 1,
      },
    ];
  },
  Scrolltoview(ID, GID) {
    let section;
    if (GID != undefined) {
      section = document.querySelector("#" + GID).parentElement.parentElement;
    } else {
      section = document.querySelector("#" + ID);
    }
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  },
  SetMinMaxZoom(view, minZoom, maxZoom) {
    var mq = window.matchMedia("screen and (min-width: 1601px)");

    if (isMobile == false) {
      if (mq.matches) {
        if (minZoom != null) view.constraints.minZoom = minZoom;
      } else {
        if (minZoom != null) view.constraints.minZoom = minZoom - 1;
      }
    }
    //for mobile
    else {
      if (minZoom != null) view.constraints.minZoom = minZoom - 2;
    }

    if (maxZoom != null) view.constraints.maxZoom = maxZoom;
  },
  addReferenceLayer(LayerURL, map) {
    if (LayerURL != null && LayerURL != "") {
      var Basemap_VectorLayer = new VectorTileLayer({
        url: LayerURL,
      });
      map.add(Basemap_VectorLayer);
    }
  },
  renderMapScale(view) {
    let scaleBar = new ScaleBar({
      view: view,
      unit: "metric",
    });
    // Add widget to the bottom left corner of the view
    view.ui.add(scaleBar, {
      position: "bottom-left",
    });
  },
  renderPrint(view) {
    const print = new Print({
      view: view,
      // specify your own print service
      printServiceUrl: process.env.REACT_APP_PrintUrl,
      //"https://webgislab.fgic.gov.ae/hosting/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task"
    });

    const printExpand = new Expand({
      expandIconClass: "esri-icon-printer", // see https://developers.arcgis.com/javascript/latest/guide/esri-icon-font/
      expandTooltip: i18next.t("Print"), // optional, defaults to "Expand" for English locale
      view: view,
      content: print,
    });

    // Add widget to the top right corner of the view
    view.ui.add(printExpand, "top-left");
    return printExpand;
  },
  createBaseMap(basemapUrl) {
    let basemap = new Basemap({
      baseLayers: [
        new VectorTileLayer({
          url: basemapUrl,
          title: "Basemap",
        }),
      ],
      title: "basemap",
      id: "basemap",
    });
    return basemap;
  },
  createBaseMapByPortalItemId() {
    let id =
      this.getSelectedLanguage() == "ar"
        ? process.env.REACT_APP_BASEMap_AR_ID
        : process.env.REACT_APP_BASEMap_EN_ID;
    let basemap = new Basemap({
      portalItem: {
        id: id, // WGS84 Streets Vector webmap
      },
    });
    return basemap;
  },
  renderSearchWidget(view, props) {
    props.PlaceHolder = props.PlaceHolder == null ? "" : props.PlaceHolder;
    props.SearchResultsTitle =
      props.SearchResultsTitle == null ? "" : props.SearchResultsTitle;
    props.ZOOMLEVEL = props.ZOOMLEVEL == null ? "" : props.ZOOMLEVEL;
    let Sources = [];
    let SeachUrl = props.SeachUrl.split(",");
    let SeachFields = props.SeachFields.split("|");
    let DisplayFields = props.DisplayFields.split(",");
    let PlaceHolder = props.PlaceHolder.split(",");
    let ZOOMLEVEL = props.ZOOMLEVEL.split(",");
    let SearchResultsTitle = props.SearchResultsTitle.split(",");
    for (var i = 0; i < SeachUrl.length; i++) {
      if (
        SeachUrl.length == SeachFields.length &&
        SeachUrl.length == DisplayFields.length
      ) {
        /* const featureLayerDistricts = new FeatureLayer({
            url: "https://webgislab.fgic.gov.ae/hosting/rest/services/Atlas_Health_Theme/HT_SL3_COVID_EN/MapServer/1",
           
          }); */
        Sources.push({
          layer: new FeatureLayer({ url: SeachUrl[i] }),
          searchFields: SeachFields[i].split(","),
          displayField: DisplayFields[i],
          exactMatch: false,
          outFields: ["*"],
          maxResults: 1000,
          ZOOMLEVEL: ZOOMLEVEL,
          name:
            SearchResultsTitle.length > 1
              ? SearchResultsTitle[i]
              : SearchResultsTitle[0],
          placeholder: PlaceHolder.length > 1 ? PlaceHolder[i] : PlaceHolder[0],
        });
      }
    }

    if (Sources.length == 0) {
      return;
    }
    const searchWidget = new Search({
      view: view,
      //allPlaceholder: "District or Senator",
      includeDefaultSources: false,
      sources: Sources,
    });

    // Add the search widget to the top left corner of the view
    view.ui.add(searchWidget, {
      position: "top-right",
    });
  },
  renderLayerList(view, Istocavailable) {
    var isopen = true;
    /* if(Istocavailable){
      isopen=true;
    } */
    if (isMobile) {
      isopen = false;
    }
    let layerList = new LayerList({
      view: view,
    });
    const layerlistExpand = new Expand({
      expandIconClass: "esri-icon-layers",
      expandTooltip: i18next.t("Toc"),
      view: view,
      content: layerList,
      expanded: isopen,
    });
    // Adds widget below other elements in the top left corner of the view
    view.ui.add(layerlistExpand, {
      position: "top-right",
    });

    return layerlistExpand;
  },
  setExtent() {
    /* const extent = new Extent({
      xmin: 51.256714,
      ymin: 22.271306,
      xmax: 56.596069,
      ymax: 26.409091,
      spatialReference: new SpatialReference({ wkid: 4326 }),
    }); */

    /* const extent = new Extent({
      xmin: 51.456868 ,
      ymin: 22.435889,
      xmax: 56.587483,
      ymax: 26.504477,
      spatialReference: new SpatialReference({ wkid: 4326 }),
    }); */

    const extent = new Extent({
      xmax: 6462104.289443189,
      xmin: 5669605.1801826935,
      ymax: 3039889.869207193,
      ymin: 2511557.129700195,
      spatialReference: new SpatialReference({ wkid: 3857 }),
    });
    return extent;
    //return null;
  },
};

export default CommonFunctions;
